import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function Conic_Gradients() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎯 Advanced CSS: Conic Gradients</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        A <b>conic gradient</b> is a gradient with color transitions that rotate
        around a center point, much like the spokes of a wheel or slices of a
        pie chart. Unlike <b>linear</b> and <b>radial</b> gradients, conic
        gradients transition <b>around a circle</b>, making them great for
        charts, color wheels, and circular effects.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Created using <code>conic-gradient()</code> or{" "}
            <code>repeating-conic-gradient()</code>.
          </li>
          <li>
            Colors are placed along an <b>angle</b> (0deg–360deg).
          </li>
          <li>
            Origin can be moved using <code>at position</code>.
          </li>
          <li>
            Perfect for <b>pie charts</b>, <b>progress indicators</b>, and{" "}
            <b>color wheels</b>.
          </li>
          <li>
            Can be combined with <code>mask</code> or <code>clip</code> for
            creative effects.
          </li>
        </ul>
      </div>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock
          code={`/* Basic syntax */
background: conic-gradient(from angle at position, color1, color2, ...);

/* Examples */
background: conic-gradient(red, yellow, green, blue);
background: conic-gradient(from 90deg at 50% 50%, #f87171, #60a5fa, #34d399);
background: repeating-conic-gradient(#22d3ee 0deg 15deg, #818cf8 15deg 30deg);`}
        />
      </div>

      {/* Basic Conic Gradient */}
      <div className="tutorial-section">
        <h2 className="section-title">🌀 Basic Conic Gradient</h2>
        <p>A simple conic gradient rotating around the center.</p>
        <div
          style={{
            background: "conic-gradient(red, yellow, green, blue, red)",
            height: "150px",
            width: "150px",
            borderRadius: "50%",
            marginBottom: "1rem",
          }}
        ></div>
        <CodeCopyBlock
          code={`div {
  background: conic-gradient(red, yellow, green, blue, red);
  border-radius: 50%;
}`}
        />
      </div>

      {/* Conic Gradient with Offset */}
      <div className="tutorial-section">
        <h2 className="section-title">📍 Conic Gradient with Offset</h2>
        <p>
          Use <code>from angle</code> and <code>at position</code> to rotate and
          reposition.
        </p>
        <div
          style={{
            background:
              "conic-gradient(from 90deg at 50% 50%, #f87171, #60a5fa, #34d399, #fbbf24)",
            height: "150px",
            width: "150px",
            borderRadius: "50%",
            marginBottom: "1rem",
          }}
        ></div>
        <CodeCopyBlock
          code={`div {
  background: conic-gradient(
    from 90deg at 50% 50%,
    #f87171,
    #60a5fa,
    #34d399,
    #fbbf24
  );
  border-radius: 50%;
}`}
        />
      </div>

      {/* Repeating Conic Gradient */}
      <div className="tutorial-section">
        <h2 className="section-title">🔁 Repeating Conic Gradient</h2>
        <p>
          Repeating conic gradients repeat at specific angles, useful for
          creating stripes, spirals, or color wheels.
        </p>
        <div
          style={{
            background:
              "repeating-conic-gradient(#22d3ee 0deg 15deg, #818cf8 15deg 30deg)",
            height: "150px",
            width: "150px",
            borderRadius: "50%",
            marginBottom: "1rem",
          }}
        ></div>
        <CodeCopyBlock
          code={`div {
  background: repeating-conic-gradient(
    #22d3ee 0deg 15deg,
    #818cf8 15deg 30deg
  );
  border-radius: 50%;
}`}
        />
      </div>

      {/* Pie Chart Example */}
      <div className="tutorial-section">
        <h2 className="section-title">📊 Conic Gradient as Pie Chart</h2>
        <p>
          Conic gradients can simulate pie charts by controlling angle stops.
        </p>
        <div
          style={{
            background:
              "conic-gradient(#f87171 0deg 90deg, #60a5fa 90deg 180deg, #34d399 180deg 270deg, #fbbf24 270deg 360deg)",
            height: "150px",
            width: "150px",
            borderRadius: "50%",
            marginBottom: "1rem",
          }}
        ></div>
        <CodeCopyBlock
          code={`div {
  background: conic-gradient(
    #f87171 0deg 90deg,
    #60a5fa 90deg 180deg,
    #34d399 180deg 270deg,
    #fbbf24 270deg 360deg
  );
  border-radius: 50%;
}`}
        />
      </div>

      {/* Overlay Example */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Overlay with Conic Gradient</h2>
        <p>
          Combine conic gradients with images and <code>rgba()</code> for unique
          overlays.
        </p>
        <div
          style={{
            background:
              "conic-gradient(rgba(0,0,0,0.5), transparent), url('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80') center/cover no-repeat",
            height: "200px",
            borderRadius: "12px",
            marginBottom: "1rem",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
            textShadow: "1px 1px 4px rgba(0,0,0,0.7)",
          }}
        >
          🌈 Conic Gradient Overlay
        </div>
        <CodeCopyBlock
          code={`div {
  background: conic-gradient(rgba(0,0,0,0.5), transparent),
              url("image.jpg") center/cover no-repeat;
}`}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Use conic gradients to create <b>donut charts</b> with CSS masks.
          </li>
          <li>
            Combine <code>conic-gradient</code> with animations for spinning
            effects.
          </li>
          <li>
            Experiment with <code>repeating-conic-gradient</code> for artistic
            patterns.
          </li>
          <li>
            Overlay with <code>radial-gradient</code> for depth and glow
            effects.
          </li>
          <li>
            Try <b>gradient borders</b> using <code>border-image</code> with
            conic gradients.
          </li>
        </ul>
      </div>
    </div>
  );
}
